import {
    Box,
    Card,
    CardBody,
    CardHeader,
    CardProps,
    Flex,
    Heading,
    Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { Cell, Pie, PieChart, Tooltip, TooltipProps } from "recharts";
import {
    NameType,
    ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { Dot } from "./dot";
import { FEE_STATUS_COLOR } from "../types/fee-status-color";

interface FeesChartProps extends CardProps {
    title: string;
    data: { name: string; value: number }[];
}

export function FeesChart({ title, data, ...props }: FeesChartProps) {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const handleMouseEnter = (index: number) => {
        setActiveIndex(index);
    };

    const handleMouseLeave = () => {
        setActiveIndex(null);
    };

    function getColor(name: string) {
        return FEE_STATUS_COLOR[name as keyof typeof FEE_STATUS_COLOR];
    }

    function formatValue(value: number, digits: number = 2) {
        return value.toLocaleString("en-GB", {
            style: "currency",
            currency: "GBP",
            minimumFractionDigits: digits,
        });
    }

    const total = data.reduce((acc, entry) => acc + entry.value, 0);

    function CustomTooltip({
        active,
        payload,
        label,
    }: TooltipProps<ValueType, NameType>) {
        if (active && payload && payload.length) {
            return (
                <Box
                    bgColor="#302E34"
                    borderRadius="8px"
                    px={4}
                    py={2}
                    whiteSpace="nowrap"
                >
                    <Flex direction="row" alignItems="center" gap={2}>
                        <Dot color={getColor(payload[0].name as string)} />
                        <Text color="#fff" fontSize="14px" fontWeight={400}>
                            {payload[0].name}
                        </Text>
                    </Flex>
                    <Text color="#fff" fontSize="14px" fontWeight={400}>
                        {formatValue(payload[0].value as number)}
                    </Text>
                </Box>
            );
        }
        return null;
    }

    return (
        <Card {...props}>
            <CardHeader pb={0}>
                <Heading
                    fontSize="18px"
                    fontWeight="600"
                    textTransform="capitalize"
                >
                    {title}
                </Heading>
            </CardHeader>
            <CardBody pt={4}>
                <Flex direction="column" gap={4}>
                    <Flex justifyContent="center">
                        <PieChart width={210} height={210}>
                            <Pie
                                data={data}
                                dataKey="value"
                                innerRadius={82.5}
                                outerRadius={102.5}
                                strokeWidth={0}
                                onMouseEnter={(_, index) =>
                                    handleMouseEnter(index)
                                }
                                onMouseLeave={handleMouseLeave}
                            >
                                {data.map((entry, index) => (
                                    <Cell
                                        key={entry.name}
                                        fill={getColor(entry.name)}
                                        stroke={
                                            activeIndex === index
                                                ? getColor(entry.name)
                                                : "#E4E3E6"
                                        }
                                        strokeWidth={
                                            activeIndex === index ? 2 : 0
                                        }
                                    />
                                ))}
                            </Pie>
                            <Tooltip
                                content={<CustomTooltip />}
                                wrapperStyle={{ zIndex: 99999 }}
                            />
                        </PieChart>
                        <Box
                            position="absolute"
                            top="50%"
                            left="50%"
                            transform="translate(-50%, -50%)"
                            textAlign="center"
                            marginTop="-40px"
                        >
                            <Box
                                fontSize="24px"
                                fontWeight="600"
                                color="#302E34"
                            >
                                {formatValue(total, 0)}
                            </Box>
                            <Box
                                fontSize="16px"
                                fontWeight="400"
                                color="#6C6B6E"
                            >
                                Total
                            </Box>
                        </Box>
                    </Flex>
                    <Flex direction="column" gap={2}>
                        {data.map((entry) => (
                            <Flex key={entry.name} alignItems="center" gap={3}>
                                <Dot color={getColor(entry.name)} />
                                <Text
                                    flex={1}
                                    fontSize="12px"
                                    fontWeight="500"
                                    color="#2E2C32"
                                >
                                    {entry.name}
                                </Text>
                                <Text
                                    align="right"
                                    fontSize="14px"
                                    fontWeight="500"
                                    color="#6C6B6E"
                                >
                                    {formatValue(entry.value)}
                                </Text>
                            </Flex>
                        ))}
                    </Flex>
                </Flex>
            </CardBody>
        </Card>
    );
}
